import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save, ArrowLeft, Package, Hash, Store, Folder, Upload, Loader2, X as XIcon } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { getImageUrl } from '../../config/imageConfig'
import useProductStore from '@/stores/productStore'
import { CreateProductDto } from '@/services/productService'
import { merchantService, Merchant } from '@/services/merchantService'
import { categoryService, Category } from '@/services/categoryService'
import { configService, Currency } from '@/services/configService'
import useAppConfigStore from '@/stores/appConfigStore'
import { DetailSection } from '../ui/DetailSection'

// Convertit une valeur date (ISO / timestamp) au format attendu par <input type="datetime-local">
const toDateTimeLocal = (value?: string | null): string => {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const ProductForm = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    currentProduct,
    loading,
    error,
    fetchProductById,
    clearError,
    clearCurrentProduct
  } = useProductStore()
  const { systemConfig } = useAppConfigStore()

  const DIETARY_TAG_OPTIONS = [
    { id: 'vegetarian', label: 'Végétarien' },
    { id: 'vegan', label: 'Vegan' },
    { id: 'gluten-free', label: 'Sans gluten' },
    { id: 'lactose-free', label: 'Sans lactose' },
    { id: 'organic', label: 'Bio' },
    { id: 'halal', label: 'Halal' },
    { id: 'kosher', label: 'Casher' },
    { id: 'nut-free', label: 'Sans noix' },
  ]

  const [formData, setFormData] = useState<CreateProductDto>({
    name: '',
    description: '',
    price: 0,
    currency: '',
    stock_quantity: 0,
    is_available: true,
    category_id: '',
    merchant_id: '',
    images: [],
    tags: [],
    dietary_tags: [],
    start_date: '',
    end_date: ''
  })

  const [tagInput, setTagInput] = useState('')
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [existingImages, setExistingImages] = useState<any[]>([]) // Images blob existantes
  const [merchants, setMerchants] = useState<Merchant[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [loadingMerchants, setLoadingMerchants] = useState(false)
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [loadingCurrencies, setLoadingCurrencies] = useState(false)
  const [merchantError, setMerchantError] = useState<string | null>(null)
  const [categoryError, setCategoryError] = useState<string | null>(null)
  const [currencyError, setCurrencyError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      fetchProductById(id)
    } else {
      clearCurrentProduct()
    }
  }, [id])

  // Load merchants, categories and currencies
  useEffect(() => {
    const loadMerchants = async () => {
      setLoadingMerchants(true)
      setMerchantError(null)
      try {
        const response = await merchantService.getAll({ limit: 100 })
        setMerchants(response.merchants || [])
      } catch (err) {
        console.error('Failed to load merchants:', err)
        setMerchantError('Impossible de charger la liste des marchands')
      } finally {
        setLoadingMerchants(false)
      }
    }

    const loadCategories = async () => {
      setLoadingCategories(true)
      setCategoryError(null)
      try {
        const response = await categoryService.getAll({})
        setCategories(response.data || [])
      } catch (err) {
        console.error('Failed to load categories:', err)
        setCategoryError('Impossible de charger la liste des catégories')
      } finally {
        setLoadingCategories(false)
      }
    }

    const loadCurrencies = async () => {
      setLoadingCurrencies(true)
      setCurrencyError(null)
      try {
        const response = await configService.currencies.getAll({ enabled: true })
        setCurrencies(response.data || [])
      } catch (err) {
        console.error('Failed to load currencies:', err)
        setCurrencyError('Impossible de charger la liste des devises')
      } finally {
        setLoadingCurrencies(false)
      }
    }

    loadMerchants()
    loadCategories()
    loadCurrencies()
  }, [])

  // Set default currency from system config when currencies are loaded
  useEffect(() => {
    if (currencies.length > 0 && systemConfig?.default_currency && !id) {
      const defaultCurrency = currencies.find(c => c.code === systemConfig.default_currency)
      if (defaultCurrency) {
        setFormData(prev => ({
          ...prev,
          currency: defaultCurrency.code
        }))
      }
    }
  }, [currencies, systemConfig, id])

  // Reset form when id changes (edit mode) or when creating new product
  useEffect(() => {
    if (id && currentProduct) {
      // Store existing images for display
      if (currentProduct.images) {
        setExistingImages(currentProduct.images);
      }
      
      // Update form with product data
      // For blob images, we don't have external URLs, so we show a placeholder
      // The images are stored as blobs in the database
      const imageUrls = currentProduct.images
        ? currentProduct.images.map(img => {
            // Use image_url if available (now stored as relative path /uploads/products/xxx.png)
            return img.image_url || `[Image: ${img.image_id}]`;
          })
        : [];
      
      setFormData({
        name: currentProduct.name || '',
        description: currentProduct.description || '',
        price: currentProduct.price || 0,
        currency: currentProduct.currency || '',
        stock_quantity: currentProduct.stock_quantity || 0,
        is_available: currentProduct.is_available ?? true,
        category_id: currentProduct.category_id || '',
        merchant_id: currentProduct.merchant_id || '',
        images: imageUrls,
        tags: currentProduct.tags || [],
        dietary_tags: currentProduct.dietary_tags || [],
        start_date: toDateTimeLocal(currentProduct.start_date),
        end_date: toDateTimeLocal(currentProduct.end_date)
      })
    } else if (!id) {
      // Reset form for new product
      setFormData({
        name: '',
        description: '',
        price: 0,
        currency: '',
        stock_quantity: 0,
        is_available: true,
        category_id: '',
        merchant_id: '',
        images: [],
        tags: [],
        dietary_tags: [],
        start_date: '',
        end_date: ''
      })
      setExistingImages([]);
    }
  }, [id, currentProduct])

  // Note: imagesInput has been removed since we no longer have URL input field

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    setUploadedFiles(prev => [...prev, ...newFiles]);

    // Create previews
    newFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });

    // Clear input
    e.target.value = '';
  };

  const removeUploadedFile = (_index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== _index));
    setImagePreviews(prev => prev.filter((_, i) => i !== _index));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : 
              type === 'checkbox' ? (e.target as HTMLInputElement).checked :
              value
    }))
  }

  const handleAddTag = () => {
    if (tagInput.trim() && !(formData.tags || []).includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()]
      }))
      setTagInput('')
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()

    // Validation des champs requis
    if (!formData.name?.trim()) {
      toast.error('Le nom du produit est requis')
      return
    }
    
    if (!formData.merchant_id) {
      toast.error('Le marchand est requis')
      return
    }
    
    if (!formData.price || formData.price <= 0) {
      toast.error('Le prix doit être supérieur à 0')
      return
    }
    
    if (!formData.stock_quantity || formData.stock_quantity <= 0) {
      toast.error('La quantité en stock doit être supérieure à 0')
      return
    }

    if (!formData.end_date) {
      toast.error('La date de fin est requise')
      return
    }

    if (formData.start_date && formData.end_date && new Date(formData.start_date) > new Date(formData.end_date)) {
      toast.error('La date de début doit être antérieure à la date de fin')
      return
    }

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Add all non-image fields
      Object.keys(formData).forEach(key => {
        if (key !== 'images') {
          const value = formData[key as keyof CreateProductDto];
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              // For arrays like tags, join them or send as JSON
              formDataToSend.append(key, JSON.stringify(value));
            } else if (typeof value === 'boolean') {
              formDataToSend.append(key, value.toString());
            } else {
              formDataToSend.append(key, value.toString());
            }
          }
        }
      });
      
      // Track deleted images
      const originalImages = currentProduct?.images || [];
      const deletedImageIds = originalImages
        .filter(originalImg => 
          !existingImages.some(existingImg => existingImg.image_id === originalImg.image_id)
        )
        .map(img => img.image_id)
        .filter(id => id);
      
      if (deletedImageIds.length > 0) {
        formDataToSend.append('deleted_image_ids', JSON.stringify(deletedImageIds));
      }
      
      // Add uploaded files directly (not as base64) - only if there are files
      if (uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          formDataToSend.append('images', file);
        });
      }
      
      // Note: We don't send image_urls because images are stored as blobs in the database
      // Existing images that haven't been deleted will be preserved automatically

      console.log('Submitting product data:', {
        uploadedFilesCount: uploadedFiles.length,
        existingImagesCount: existingImages.length,
        deletedImageIds,
        formDataKeys: Array.from(formDataToSend.keys())
      });

      // Use the product store's create/update methods which should handle FormData
      if (id) {
        // For update, we need to send as FormData
        // First, let's try to use the store's update method with FormData
        const response = await fetch(`/api/products/products/${id}`, {
          method: 'PUT',
          body: formDataToSend,
          headers: {
            'Authorization': localStorage.getItem('access_token') ? `Bearer ${localStorage.getItem('access_token')}` : ''
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        
        const result = await response.json();
        if (!result.success) {
          throw new Error(result.message || 'Échec de la mise à jour du produit');
        }
      } else {
        // For create, use FormData directly
        const response = await fetch('/api/products/products', {
          method: 'POST',
          body: formDataToSend,
          headers: {
            'Authorization': localStorage.getItem('access_token') ? `Bearer ${localStorage.getItem('access_token')}` : ''
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        
        const result = await response.json();
        if (!result.success) {
          throw new Error(result.message || 'Échec de la création du produit');
        }
      }
      navigate('/products')
    } catch (error) {
      console.error('Error saving product:', error)
      // Show error in UI
      clearError();
      // Set error in store or show toast
    }
  }

  // Compute currency symbol - defensive implementation
  const currencySymbol = useMemo(() => {
    try {
      if (!systemConfig?.default_currency) return ''
      if (!currencies || !Array.isArray(currencies)) return systemConfig.default_currency
      const currency = currencies.find(c => c.code === systemConfig.default_currency)
      return currency?.symbol || systemConfig.default_currency
    } catch (error) {
      console.error('Error computing currency symbol:', error)
      return ''
    }
  }, [systemConfig, currencies])

  // Show loading skeleton when loading product data in edit mode
  if (id && loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="h-10 w-24 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="col-span-2 md:col-span-1">
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mb-2"></div>
                <div className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-6 border-t border-gray-200">
            <div className="h-12 w-32 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    )
  }

  // Show error if product failed to load
  if (id && !loading && !currentProduct) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/products')}
              className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Retour à la liste
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Modifier le produit</h1>
            <p className="text-gray-600">Modifiez les informations du produit</p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="font-medium">Impossible de charger le produit</p>
          <p className="text-sm mt-1">Le produit avec l'ID "{id}" n'a pas pu être chargé. Veuillez vérifier que le produit existe.</p>
          <button
            onClick={() => fetchProductById(id)}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Réessayer
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/products')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour à la liste
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {id ? 'Modifier le produit' : 'Nouveau produit'}
          </h1>
          <p className="text-gray-600">
            {id ? 'Modifiez les informations du produit' : 'Ajoutez un nouveau produit à la plateforme'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Informations du produit */}
          <DetailSection title="Informations du produit" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">

            {/* Nom */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du produit *
              </label>
              <div className="relative">
                <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Nom du produit"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                placeholder="Description détaillée du produit"
              />
            </div>

            {/* Catégorie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Catégorie *
              </label>
              <div className="relative">
                <Folder className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                {loadingCategories ? (
                  <div className="animate-pulse h-10 w-full bg-gray-200 rounded-lg"></div>
                ) : categoryError ? (
                  <div>
                    <select
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleChange}
                      required
                      className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                    >
                      <option value="">Sélectionner une catégorie</option>
                    </select>
                    <p className="text-xs text-red-500 mt-1">{categoryError}</p>
                  </div>
                ) : (
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    required
                    className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map((category) => (
                      <option key={category.category_id} value={category.category_id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Marchand */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marchand *
              </label>
              <div className="relative">
                <Store className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                {loadingMerchants ? (
                  <div className="animate-pulse h-10 w-full bg-gray-200 rounded-lg"></div>
                ) : merchantError ? (
                  <div>
                    <select
                      name="merchant_id"
                      value={formData.merchant_id}
                      onChange={handleChange}
                      required
                      className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                    >
                      <option value="">Sélectionner un marchand</option>
                    </select>
                    <p className="text-xs text-red-500 mt-1">{merchantError}</p>
                  </div>
                ) : (
                  <select
                    name="merchant_id"
                    value={formData.merchant_id}
                    onChange={handleChange}
                    required
                    className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="">Sélectionner un marchand</option>
                    {merchants.map((merchant) => (
                      <option key={merchant.merchant_id} value={merchant.merchant_id}>
                        {merchant.business_name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </DetailSection>

          {/* Tarification et stock */}
          <DetailSection title="Tarification et stock" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">

            {/* Prix */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prix *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium">
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Devise */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Devise *
              </label>
              {loadingCurrencies ? (
                <div className="animate-pulse h-10 w-full bg-gray-200 rounded-lg"></div>
              ) : currencyError ? (
                <div>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                    disabled
                  >
                    <option value="">Erreur de chargement des devises</option>
                  </select>
                  <p className="text-xs text-red-500 mt-1">{currencyError}</p>
                </div>
              ) : currencies.length === 0 ? (
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                  disabled
                >
                  <option value="">Aucune devise disponible</option>
                </select>
              ) : (
                <select
                  name="currency"
                  value={formData.currency || ''}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">Sélectionnez une devise</option>
                  {currencies.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.name} ({currency.code}) - {currency.symbol}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Stock */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantité en stock *
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="number"
                  name="stock_quantity"
                  value={formData.stock_quantity}
                  onChange={handleChange}
                  required
                  min="0"
                  className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Disponibilité */}
            <div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_available"
                  name="is_available"
                  checked={formData.is_available}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_available: e.target.checked }))}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_available" className="ml-2 block text-sm text-gray-900">
                  Produit disponible à la vente
                </label>
              </div>
            </div>

            {/* Date de début */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de début
              </label>
              <input
                type="datetime-local"
                name="start_date"
                value={formData.start_date || ''}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
              />
            </div>

            {/* Date de fin */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de fin <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                name="end_date"
                value={formData.end_date || ''}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </DetailSection>
        </div>

        {/* Images du produit */}
        <DetailSection title="Images du produit" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">

          {/* File upload */}
          <div className="mb-4">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="h-8 w-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">
                  <span className="font-semibold">Cliquez pour téléverser</span> ou glissez-déposez
                </p>
                <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF jusqu'à 5MB</p>
              </div>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
          </div>

          {/* Existing images previews */}
          {existingImages.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Images existantes:</p>
              <div className="grid grid-cols-4 gap-2">
                {existingImages.map((img, index) => (
                  <div key={index} className="relative">
                    {img.image_url ? (
                      <img
                        src={getImageUrl(img.image_url)}
                        alt={`Image ${index + 1}`}
                        className="h-24 w-full object-cover rounded-lg"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const parent = e.currentTarget.parentElement;
                          if (parent) {
                            parent.innerHTML = `<div class="h-24 w-full bg-gray-200 rounded-lg flex items-center justify-center"><span class="text-gray-500 text-sm">Image ${index + 1}</span></div>`;
                          }
                        }}
                      />
                    ) : (
                      <div className="h-24 w-full bg-gray-200 rounded-lg flex items-center justify-center">
                        <span className="text-gray-500 text-sm">Image ${img.image_id || (index + 1)}</span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        // Remove image from existing images
                        setExistingImages(prev => prev.filter((_, i) => i !== index));
                        // Also update formData.images to remove the placeholder
                        const updatedImages = formData.images?.filter((_, i) => i !== index) || [];
                        setFormData(prev => ({ ...prev, images: updatedImages }));
                      }}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Les images existantes sont stockées en base de données. Cliquez sur la croix pour supprimer une image.
              </p>
            </div>
          )}

          {/* Image previews for newly uploaded files */}
          {imagePreviews.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Nouvelles images uploadées:</p>
              <div className="grid grid-cols-4 gap-2">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="relative">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="h-24 w-full object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeUploadedFile(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Note pour les images existantes */}
          {id && existingImages.length > 0 && (
            <div className="mt-4">
              <p className="text-xs text-blue-600">
                Note : Les images existantes sont affichées ci-dessus. Elles seront conservées si vous ne téléversez pas de nouvelles images.
              </p>
            </div>
          )}
        </DetailSection>

        {/* Classification */}
        <DetailSection title="Classification" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                placeholder="Ajouter une étiquette"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Ajouter
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(formData.tags || []).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-2 text-primary-600 hover:text-primary-900"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Dietary Tags */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dietary Tags
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Select dietary classifications for this product
            </p>
            <div className="flex flex-wrap gap-2">
              {DIETARY_TAG_OPTIONS.map((tag) => {
                const isSelected = (formData.dietary_tags || []).includes(tag.id)
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        dietary_tags: isSelected
                          ? (prev.dietary_tags || []).filter(t => t !== tag.id)
                          : [...(prev.dietary_tags || []), tag.id]
                      }))
                    }}
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      isSelected
                        ? 'bg-primary-100 text-primary-800 ring-2 ring-primary-400'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {tag.label}
                  </button>
                )
              })}
            </div>
          </div>
        </DetailSection>

        {/* Actions */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/products')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 inline mr-2" />
                {id ? 'Mettre à jour' : 'Créer le produit'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default ProductForm
